import React from 'react';
import { AxiosError } from 'axios';
import { isPast } from 'date-fns';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { HRCalendarEvent } from '../../../providers/api/dtos';
import { HRCalendarEventComputedStatusType } from '../../types';
import { useJobApplication } from '../../../job-application/hooks/use-job-application/useJobApplication';
import { HRInterviewComputedStatus, HRInterviewStatus, HRRecallComputedStatus } from '../../constants';
import { removeNullAndUndefinedValues } from '../../../utils/array';

export interface UseComputedHRCalendarEventStatusData {
  status: HRCalendarEventComputedStatusType,
  canBeUpdated: boolean,
  canBeUpdatedTo: (status: HRCalendarEventComputedStatusType) => boolean,
}

export type UseComputedHRCalendarEventStatusResult = {
  data?: UseComputedHRCalendarEventStatusData,
  isLoading: boolean,
  errors?: Error[] | undefined,
  mutate: VoidFunction,
};

const computedStatusCanBeUpdatedTo: Record<HRCalendarEventComputedStatusType, HRCalendarEventComputedStatusType[]> = {
  [HRInterviewComputedStatus.Todo]: [HRInterviewStatus.Cancelled],
  [HRInterviewComputedStatus.Done]: [],
  [HRInterviewComputedStatus.Cancelled]: [],
  [HRInterviewComputedStatus.ToProcess]: [HRInterviewStatus.Cancelled, HRInterviewStatus.Done],
  [HRRecallComputedStatus.Active]: [HRRecallComputedStatus.Done],
  [HRRecallComputedStatus.Expired]: [HRRecallComputedStatus.Done],
};

export const useComputedHRCalendarEventStatus = (calendarEvent: HRCalendarEvent): UseComputedHRCalendarEventStatusResult => {
  const [errors, setErrors] = React.useState<Error[] | undefined>();

  const handleNewError = React.useCallback((err: AxiosError) => {
    setErrors((prev) => {
      if (!prev) return [err];
      return [...prev, err];
    });
  }, []);

  const { user, userIsAdmin, userIsHR } = useRBAC();

  const {
    data: jobApplication,
    isLoading: isJobApplicationLoading,
    mutate: mutateJobApplication,
  } = useJobApplication(calendarEvent.jobApplicationId, {
    onError: handleNewError,
  });

  const computedStatus = React.useMemo<HRCalendarEventComputedStatusType>(() => {
    switch (calendarEvent.type) {
      case 'RECALL':
        if (calendarEvent.isResolved) return HRRecallComputedStatus.Done;
        if (isPast(calendarEvent.date)) return HRRecallComputedStatus.Expired;
        return HRRecallComputedStatus.Active;

      case 'INTERVIEW':
        if (calendarEvent.status === HRInterviewStatus.Todo && isPast(calendarEvent.date)) {
          return HRInterviewComputedStatus.ToProcess;
        }
        return calendarEvent.status;
      default:
        throw new Error('unknown calendar event type');
    }
  }, [calendarEvent]);

  const canBeUpdatedTo = React.useMemo(() => {
    const userCanManageRecall = calendarEvent.type === 'RECALL'
      && (userIsAdmin
        || userIsHR
        || jobApplication?.supervisorEmail === user.email);

    const userCanManageInterview = calendarEvent.type === 'INTERVIEW'
      && (userIsAdmin
        || userIsHR
        || removeNullAndUndefinedValues([jobApplication?.supervisorEmail, jobApplication?.assigneeEmail, calendarEvent.interviewerEmail]).includes(user.email));

    if (userCanManageRecall || userCanManageInterview) {
      return computedStatusCanBeUpdatedTo[computedStatus];
    }

    return [];
  }, [calendarEvent, computedStatus, jobApplication?.assigneeEmail, jobApplication?.supervisorEmail, user.email, userIsAdmin, userIsHR]);

  const canBeUpdatedToFn = React.useCallback(
    (status: HRCalendarEventComputedStatusType) => canBeUpdatedTo.includes(status),
    [canBeUpdatedTo],
  );

  const data: UseComputedHRCalendarEventStatusData | undefined = React.useMemo(() => {
    if (errors || isJobApplicationLoading) return undefined;

    return {
      status: computedStatus,
      canBeUpdated: canBeUpdatedTo.length > 0,
      canBeUpdatedTo: canBeUpdatedToFn,
    };
  }, [canBeUpdatedTo.length, canBeUpdatedToFn, computedStatus, errors, isJobApplicationLoading]);

  return React.useMemo(() => ({
    data,
    isLoading: isJobApplicationLoading,
    errors,
    mutate: mutateJobApplication,
  }), [data, errors, isJobApplicationLoading, mutateJobApplication]);
};
